import { components } from "@appretio/api";
import type { GridApi } from "ag-grid-enterprise";
import { WEEKLY_KEYS } from "../../../utils/timePeriodUtils";

type IntakeGridData = components["schemas"]["IntakeGridData"];

/** Where the grid's intake component metrics come from. */
export type IntakeSource = components["schemas"]["IntakeSource"];

/**
 * The source a grid shows before its first payload lands. A payload names the
 * source it carries (`main_intake_source`); every other source is an overlay.
 */
export const INITIAL_INTAKE_SOURCE: IntakeSource = "ETA";

/**
 * Whether a source's intake rows may be edited and saved. Planned is the
 * cube's own intake; Confirmed and ETA are the same warehouse orders, placed
 * on their delivery week and on the week they are expected to land.
 */
const INTAKE_SOURCE_EDITABLE: Record<IntakeSource, boolean> = {
  Planned: true,
  Confirmed: false,
  ETA: false
};

/** Every intake source, in toggle order. */
export const INTAKE_SOURCES = Object.keys(
  INTAKE_SOURCE_EDITABLE
) as IntakeSource[];

export function isIntakeSource(value: string): value is IntakeSource {
  return Object.hasOwn(INTAKE_SOURCE_EDITABLE, value);
}

export function isEditableIntakeSource(
  source: IntakeSource | undefined
): boolean {
  return source != null && INTAKE_SOURCE_EDITABLE[source];
}

/**
 * The intake tabs a page shows. A budget cube holds no intake, so the save
 * would reject its Planned edits; the budget page shows no Planned tab.
 */
export function shownIntakeSources(isBudget: boolean): IntakeSource[] {
  return isBudget
    ? INTAKE_SOURCES.filter((source) => !isEditableIntakeSource(source))
    : INTAKE_SOURCES;
}

/**
 * The 8 irreducible intake component metrics. These are the metrics
 * that get swapped when toggling between intake sources. They are editable
 * only for an editable source. Totals and AUC metrics are derived/calculated
 * and not included.
 */
export const INTAKE_COMPONENT_METRICS = new Set([
  "INTAKE_AT_COST_INSEASON",
  "INTAKE_AT_COST_MAIN",
  "INTAKE_AT_COST_REPLENISHMENT",
  "INTAKE_AT_COST_CLEARANCE",
  "INTAKE_UNITS_INSEASON",
  "INTAKE_UNITS_MAIN",
  "INTAKE_UNITS_REPLENISHMENT",
  "INTAKE_UNITS_CLEARANCE"
]);

/**
 * The stock an intake source moves. A closed week's stock holds the open
 * orders due by then, and the first open week's BOP seeds the projection.
 */
export const STOCK_METRICS = new Set([
  "BEGINNING_OF_PERIOD_UNITS",
  "END_OF_PERIOD_UNITS",
  "BEGINNING_OF_PERIOD_AT_COST",
  "END_OF_PERIOD_AT_COST"
]);

/**
 * The rows a toggle swaps: the intake components and the stock they move. A
 * source leaves a week blank where the grid keeps its value.
 */
export const SOURCE_METRICS = new Set([
  ...INTAKE_COMPONENT_METRICS,
  ...STOCK_METRICS
]);

export type IntakeCache = Map<string, Record<string, number | null>>;

/** One intake cache per source. The source on the grid has none. */
export type IntakeCaches = Partial<Record<IntakeSource, IntakeCache>>;

/**
 * Creates a composite key for identifying an intake row.
 */
export function intakeRowKey(row: {
  metric_name: string;
  fiscal_year_name: string;
  producer_type: string;
}): string {
  return `${row.metric_name}|${row.fiscal_year_name}|${row.producer_type}`;
}

/** The source rows among ``rows``, keyed by composite row key with weekly values. */
export function intakeRowsToCache(
  rows: Iterable<Record<string, unknown>>
): IntakeCache {
  const cache: IntakeCache = new Map();
  for (const row of rows) {
    if (!SOURCE_METRICS.has(String(row.metric_name))) continue;
    const weekValues: Record<string, number | null> = {};
    for (const wk of WEEKLY_KEYS) {
      if (row[wk] != null) weekValues[wk] = row[wk] as number;
    }
    cache.set(
      intakeRowKey(row as Parameters<typeof intakeRowKey>[0]),
      weekValues
    );
  }
  return cache;
}

/**
 * ``target`` over the main source's rows: a week or row ``target`` leaves
 * blank shows the main source's value, not whichever source the grid shows.
 */
export function overMainSource(
  caches: IntakeCaches,
  target: IntakeCache,
  mainSource: IntakeSource
): IntakeCache {
  const main = caches[mainSource];
  if (!main) return target;
  const merged: IntakeCache = new Map();
  for (const [key, weeks] of main) merged.set(key, { ...weeks });
  for (const [key, weeks] of target) {
    merged.set(key, { ...merged.get(key), ...weeks });
  }
  return merged;
}

/** {@link intakeRowsToCache} over the rows the grid holds. */
export function extractIntakeRowsFromGrid(api: GridApi): IntakeCache {
  const rows: Record<string, unknown>[] = [];
  api.forEachNode((node) => {
    if (node.data) rows.push(node.data);
  });
  return intakeRowsToCache(rows);
}

/**
 * Injects cached source values into the grid, overwriting the weeks the cache
 * holds on matching source rows. Returns the rows as written.
 */
export function injectIntakeIntoGrid(
  api: GridApi,
  intakeCache: IntakeCache
): Record<string, unknown>[] {
  const written: Record<string, unknown>[] = [];
  api.forEachNode((node) => {
    const data = node.data;
    if (!data) return;
    if (!SOURCE_METRICS.has(data.metric_name)) return;

    const key = intakeRowKey(data);
    const cached = intakeCache.get(key);
    if (!cached) return;

    const updatedData = { ...data };
    for (const wk of WEEKLY_KEYS) {
      if (wk in cached) {
        updatedData[wk] = cached[wk];
      }
    }
    node.setData(updatedData);
    written.push(updatedData);
  });
  return written;
}

/**
 * One source's overlay from the API's `intake_data` (rows per producer_type,
 * e.g. "user", "budget") as an IntakeCache.
 */
export function intakeOverlayToCache(
  overlay: Record<string, IntakeGridData[]>
): IntakeCache {
  return intakeRowsToCache(Object.values(overlay).flat());
}

/** Converts the API's `intake_data` (rows per source per producer) into caches. */
export function intakeDataToCaches(
  apiData: Record<string, Record<string, IntakeGridData[]>>
): IntakeCaches {
  const caches: IntakeCaches = {};
  for (const [source, rows] of Object.entries(apiData)) {
    if (isIntakeSource(source)) caches[source] = intakeOverlayToCache(rows);
  }
  return caches;
}

/** Returns whether ``metricName`` is an intake metric, component or total. */
export function isIntakeMetric(metricName: unknown): boolean {
  return String(metricName ?? "").startsWith("INTAKE");
}

/** Returns whether ``rows`` carry any intake metric. */
export function hasIntakeRows(rows: Array<Record<string, unknown>>): boolean {
  return rows.some((row) => isIntakeMetric(row.metric_name));
}
